import type { OpponentTradeData } from '../../types/interfaces';
import { useGameFlowStore } from '../../stores/gameFlowStore';
import { useGameSessionStore } from '../../stores/gameSessionStore';
import { useFriendStore } from '../../stores/friendStore';
import { decompressData } from '../decryptApiPayload';
import type { FriendMatchInvitePayload, ReceiveInviteIDPayload } from './hubEvents';
import {
  normalizeFriendStatusChangedPayload,
  normalizeFriendsOnlineSnapshotPayload,
  normalizeMatchFoundPayload,
  normalizeOpponentLeftPayload,
  normalizeReceiveGameRoomIDPayload,
  normalizeReceiveMatchIDPayload,
} from './hubEvents';

export const signalREventDispatcher = {
  MatchFound: (data: unknown) => {
    useGameFlowStore.getState().onMatchFound(normalizeMatchFoundPayload(data));
  },

  OpponentLeft: (data: unknown) => {
    useGameFlowStore.getState().onOpponentLeft(normalizeOpponentLeftPayload(data));
  },

  ReceiveGameRoomID: (data: unknown) => {
    useGameFlowStore.getState().onReceiveGameRoomID(normalizeReceiveGameRoomIDPayload(data));
  },

  ReceiveFriendMatchInvite: (data: FriendMatchInvitePayload) => {
    useGameFlowStore.getState().onReceiveFriendMatchInvite(data);
  },

  ReceiveMatchID: (data: unknown) => {
    useGameFlowStore.getState().onReceiveMatchID(normalizeReceiveMatchIDPayload(data));
  },

  ReceiveInviteID: (data: ReceiveInviteIDPayload) => {
    useGameFlowStore.getState().onReceiveInviteID(data);
  },

  CancelFriendMatch: () => {
    useGameFlowStore.getState().onCancelFriendMatch();
  },

  FriendMatchRejected: () => {
    useGameFlowStore.getState().onFriendMatchRejected();
  },

  ReceiveTrade: (data: {
    symbolTradeCount?: Record<string, { long: number; short: number }>;
    symbolProfitLoss?: Record<string, number>;
    remainingFunds?: number;
    totalBalanceTmp?: number;
    symbolTradeState?: Record<string, unknown>;
    currentIndex?: number;
    compressedTradeEntryHistory?: string | null;
  }) => {
    const session = useGameSessionStore.getState();
    session.setOpponentTradeData({
      TradeCount: data.symbolTradeCount ?? {},
      ProfitLoss: data.symbolProfitLoss ?? {},
      Funds: data.remainingFunds ?? 1_000_000,
      TotalBalance: data.totalBalanceTmp ?? 1_000_000,
      SymbolTradeState: (data.symbolTradeState ?? {}) as never,
      CurrentIndex: (data.currentIndex ?? 0) as OpponentTradeData['CurrentIndex'],
    });

    const compressed = data.compressedTradeEntryHistory;
    if (compressed != null) {
      queueMicrotask(() => {
        try {
          const history = decompressData(compressed);
          useGameSessionStore.getState().appendTradeHistory(history);
        } catch (err) {
          console.warn('?????????:', err);
        }
      });
    }
  },

  OpponentLeftGame: () => {
    useGameSessionStore.getState().setOpponentLeftGame(true);
  },

  OpponentSceneReady: (remoteGameRoomId: string) => {
    useGameFlowStore.getState().onOpponentSceneReady(remoteGameRoomId);
    useGameSessionStore.getState().notifyOpponentSceneReady();
  },

  SyncGameStart: (startTimestamp: number) => {
    useGameSessionStore.getState().onSyncGameStart(startTimestamp);
  },

  FriendStatusChanged: (data: unknown) => {
    const payload = normalizeFriendStatusChangedPayload(data);
    if (!Number.isFinite(payload.userId) || !Number.isFinite(payload.status)) return;
    useFriendStore.getState().updateFriendStatus(payload.userId, payload.status);
  },

  FriendsOnlineSnapshot: (data: unknown) => {
    useFriendStore.getState().applyOnlineSnapshot(normalizeFriendsOnlineSnapshotPayload(data));
  },
} as const;

export type SignalREventName = keyof typeof signalREventDispatcher;
